import React from 'react';
import { useDropzone } from 'react-dropzone';

import { UxContext } from '@client/context/UxContext';
import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import { Divider, Grid } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

import CloudRestoreDialog, { type CloudBackupEntry } from '../CloudRestoreDialog';

interface BackupTabProps {
    serverOffline: boolean;
    initialCloudBackupEnabled: boolean;
    onBlock: (text: string | null) => void;
}

function BackupTab({ serverOffline, initialCloudBackupEnabled, onBlock }: BackupTabProps) {
    const { handleSnackbar, handleAlert } = React.useContext(UxContext)!;

    const [importFile, setImportFile] = React.useState<File | null>(null);
    const [importing, setImporting] = React.useState(false);
    const [cloudBackupEnabled, setCloudBackupEnabled] = React.useState(false);
    const [cloudRestoreOpen, setCloudRestoreOpen] = React.useState(false);
    const [cloudBackups, setCloudBackups] = React.useState<CloudBackupEntry[]>([]);
    const [cloudBackupsLoading, setCloudBackupsLoading] = React.useState(false);
    const [currentVersion, setCurrentVersion] = React.useState<string | null>(null);

    React.useEffect(() => {
        setCloudBackupEnabled(initialCloudBackupEnabled);
    }, [initialCloudBackupEnabled]);

    React.useEffect(() => {
        lediumApi.get<{ version: string }>('update/getVersion').then((data) => {
            if (data) {
                setCurrentVersion(data.version);
            }
        });
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: { 'application/zip': ['.zip'] },
        multiple: false,
        onDrop: (accepted) => {
            if (accepted[0]) {
                setImportFile(accepted[0]);
            }
        }
    });

    const offlineDisabledSection = serverOffline ? { opacity: 0.5, pointerEvents: 'none' as const } : undefined;
    const SectionOfflineIcon = () => (serverOffline ? <WifiOffIcon sx={{ color: 'error.main', fontSize: '1.2em' }} /> : null);

    async function onCloudBackupToggle(event: React.ChangeEvent<HTMLInputElement>) {
        const enabled = event.target.checked;
        setCloudBackupEnabled(enabled);
        const result = await lediumApi.patch('settings/cloudBackupEnabled', { enabled });
        if (result) {
            handleSnackbar({ message: i18n('settings_saved') as string });
        } else {
            handleSnackbar({ message: i18n('settings_save_error') as string, severity: 'error' });
        }
    }

    async function onImport() {
        if (!importFile) {
            return;
        }
        setImporting(true);
        const form = new FormData();
        form.append('database', importFile, importFile.name);
        const response = await lediumApi.postForm<{ success: boolean; error?: string }>('settings/importDatabase', form);
        setImporting(false);
        if (!response) {
            return;
        }
        if (response.success) {
            onBlock(i18n('restoring_database') as string);
        } else {
            const key = `import_database_error_${response.error ?? 'unknown'}`;
            handleSnackbar({
                message: (i18n(key) as string) || (i18n('import_database_error_unknown') as string),
                severity: 'error'
            });
        }
    }

    function onCloudRestore(backup: CloudBackupEntry) {
        handleAlert({
            title: i18n('cloud_restore_confirm_title') as string,
            message: i18n('cloud_restore_confirm_message', backup.version, backup.date) as string,
            okText: i18n('cloud_restore_cta') as string,
            cancelText: i18n('cancel') as string,
            destructive: true,
            okCallback: async () => {
                setCloudRestoreOpen(false);
                setImporting(true);
                const response = await lediumApi.post<{ success: boolean; error?: string }>('settings/cloudRestore', { filename: backup.filename });
                setImporting(false);
                if (!response) {
                    return;
                }
                if (response.success) {
                    onBlock(i18n('restoring_database') as string);
                } else {
                    const key = `import_database_error_${response.error ?? 'unknown'}`;
                    handleSnackbar({
                        message: (i18n(key) as string) || (i18n('import_database_error_unknown') as string),
                        severity: 'error'
                    });
                }
            }
        });
    }

    return (
        <>
            <Grid container spacing={3} sx={{ marginTop: '20px' }}>
                <Grid item xs={12}>
                    <Typography variant="h2">{i18n('export_database')}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ marginTop: '4px' }}>
                        {i18n('export_database_description')}
                    </Typography>
                </Grid>

                <Grid item xs={12} sx={{ textAlign: 'center' }}>
                    <Button variant="contained" color="primary" href="/api/settings/exportDatabase" download>
                        {i18n('export_database_download')}
                    </Button>
                </Grid>
            </Grid>

            <Divider sx={{ marginTop: '32px' }} />

            <Grid container spacing={3} sx={{ marginTop: '20px' }}>
                <Grid item xs={12}>
                    <Typography variant="h2">{i18n('import_database')}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ marginTop: '4px' }}>
                        {i18n('import_database_description')}
                    </Typography>
                </Grid>

                <Grid item xs={12}>
                    <Box
                        {...getRootProps()}
                        sx={{
                            border: '2px dashed',
                            borderColor: isDragActive ? 'primary.main' : 'divider',
                            borderRadius: 1,
                            p: 4,
                            textAlign: 'center',
                            cursor: 'pointer',
                            bgcolor: isDragActive ? 'action.hover' : 'transparent',
                            transition: 'border-color 0.2s, background-color 0.2s'
                        }}
                    >
                        <input {...getInputProps()} />
                        <Typography variant="body2" color="text.secondary">
                            {importFile ? importFile.name : i18n('import_database_drop_or_select')}
                        </Typography>
                    </Box>
                </Grid>

                <Grid item xs={12} sx={{ textAlign: 'center' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={!importFile || importing}
                        onClick={onImport}
                        startIcon={importing ? <CircularProgress size={16} color="inherit" /> : undefined}
                    >
                        {i18n('import_database_upload')}
                    </Button>
                </Grid>
            </Grid>

            <Divider sx={{ marginTop: '32px' }} />

            <Grid container spacing={3} sx={{ marginTop: '20px' }}>
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SectionOfflineIcon />
                        <Typography variant="h2" color={serverOffline ? 'text.secondary' : undefined}>
                            {i18n('cloud_backup')}
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ marginTop: '4px' }}>
                        {i18n('cloud_backup_description')}
                    </Typography>
                </Grid>
                <Grid item xs={12} sx={{ textAlign: 'center', ...offlineDisabledSection }}>
                    <FormControlLabel
                        control={<Switch checked={cloudBackupEnabled} onChange={onCloudBackupToggle} color="primary" />}
                        label={i18n('cloud_backup_enabled') as string}
                    />
                </Grid>
            </Grid>

            <Divider sx={{ marginTop: '32px' }} />

            <Grid container spacing={3} sx={{ marginTop: '20px' }}>
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SectionOfflineIcon />
                        <Typography variant="h2" color={serverOffline ? 'text.secondary' : undefined}>
                            {i18n('cloud_restore')}
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ marginTop: '4px' }}>
                        {i18n('cloud_restore_description')}
                    </Typography>
                </Grid>
                <Grid item xs={12} sx={{ textAlign: 'center', ...offlineDisabledSection }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setCloudRestoreOpen(true);
                            setCloudBackupsLoading(true);
                            lediumApi.get<{ backups: CloudBackupEntry[] }>('settings/cloudBackups').then((data) => {
                                setCloudBackups(data?.backups ?? []);
                                setCloudBackupsLoading(false);
                            });
                        }}
                    >
                        {i18n('cloud_restore_open')}
                    </Button>
                </Grid>
            </Grid>

            <CloudRestoreDialog
                isOpen={cloudRestoreOpen}
                onClose={() => setCloudRestoreOpen(false)}
                loading={cloudBackupsLoading}
                backups={cloudBackups}
                currentVersion={currentVersion}
                onRestore={onCloudRestore}
            />
        </>
    );
}

export default BackupTab;
